import type { RefObject } from "react";
import { useEffect, useMemo, useRef } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { PageContainer } from "app/pages/industry-analysis/common/styledComponents";
import { EXCEL_ENDPOINT, EXCEL_ENDPOINT_WEBSITES_ONLY } from "app/pages/industry-analysis/players/const";
import { useMarketPlayersData } from "app/pages/industry-analysis/players/hooks/useMarketPlayersData";
import MarketShares from "app/pages/industry-analysis/players/market-shares/MarketShares";
import { BubbleChartWrapper } from "app/pages/industry-analysis/players/table/bubble-chart/BubbleChartWrapper";
import { Table } from "app/pages/industry-analysis/players/table/Table";
import type { IRawData } from "app/pages/industry-analysis/players/table/types";
import type { IMarketPlayersState } from "app/pages/industry-analysis/players/types";
import { EScrollTo } from "app/pages/industry-analysis/players/types";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import { BasicDurations } from "app/services/DurationService";

import { RisingAndDecliningPlayers } from "./rising-and-declining-players/RisingAndDecliningPlayers";
import { getUseDomainsOnly } from "./utils/getUseDomainsOnly";

const EMPTY_RAW_DATA: IRawData = { Data: [], TotalCount: 0, ConversionRatesAvailable: false };

export const MarketPlayers = () => {
    const { data, isLoading } = useMarketPlayersData();

    const rawData = data?.rawData ?? EMPTY_RAW_DATA;
    const risingAndDecliningRawData = data?.risingAndDecliningRawData ?? EMPTY_RAW_DATA;

    const state: IMarketPlayersState = useMemo(
        () => ({
            rawData,
            risingAndDecliningRawData,
            isLoading,
        }),
        [rawData, risingAndDecliningRawData, isLoading],
    );

    const useDomainsOnly = getUseDomainsOnly();

    const { params } = useCurrentStateAndParams();
    const { scrollTo, category } = params as { scrollTo?: EScrollTo; category?: string };
    const isCustomIndustry = UserCustomCategoryService.isCustomCategory(category ?? "");
    const bubbleChartAndTableRef = useRef<HTMLDivElement>(null);
    const marketSharesRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setTimeout(() => {
            if (!scrollTo || isLoading) {
                return;
            }

            const scrollBehavior: ScrollIntoViewOptions = { behavior: "smooth" };
            const scrollRefMap: Record<EScrollTo, RefObject<HTMLDivElement>> = {
                [EScrollTo.QUADRANT]: bubbleChartAndTableRef,
                [EScrollTo.MARKET_SHARES]: marketSharesRef,
            };
            const targetWidgetRef = scrollRefMap[scrollTo]?.current;

            if (!targetWidgetRef) {
                return;
            }
            targetWidgetRef.scrollIntoView(scrollBehavior);
        }, 0);
    }, [isLoading, scrollTo]);

    const excelEndpoint = useDomainsOnly ? EXCEL_ENDPOINT_WEBSITES_ONLY : EXCEL_ENDPOINT;
    const isDaily = params.duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const dataSet = isDaily ? EMPTY_RAW_DATA : rawData;

    return (
        <PageContainer>
            <div ref={marketSharesRef}>
                <MarketShares isDaily={isDaily} />
            </div>
            <RisingAndDecliningPlayers state={state} />
            <div ref={bubbleChartAndTableRef}>
                <BubbleChartWrapper
                    isLoading={isLoading}
                    rawData={dataSet}
                />
                <Table
                    state={state}
                    excelEndpoint={excelEndpoint}
                    isCustomIndustry={isCustomIndustry}
                    isDaily={isDaily}
                />
            </div>
        </PageContainer>
    );
};
